import { GemsBadge } from "@/app/games/(details)/quest-badges";
import { headingPrimary } from "@/constants";
import { Action, UserJourney } from "@/context/AnalyticsProvider";
import { useWishlistHandler } from "@/hooks/useWishlistHandler";
import { notifyError } from "@/utils/monitoring";
import { Gamepad2, Heart, Loader2 } from "lucide-react";
import Image from "next/image";
import { useState } from "react";
import { Button } from "../../button";
import { useHeroCarousel } from "./useHeroCarousel";

export const CarouselContent = () => {
  const { currentSlide, currentIndex, router, track } = useHeroCarousel();

  // Wishlist handler same as games page
  const { isWishlisted, addToWishlist, removeFromWishlist, isWishlisting } =
    useWishlistHandler(currentSlide.gameId || "", undefined, undefined, false);

  const [isLoading, setIsLoading] = useState(false);

  const handleFollowToggle = async () => {
    if (!currentSlide.gameId) return;

    setIsLoading(true);

    try {
      if (isWishlisted) {
        await removeFromWishlist();
      } else {
        await addToWishlist();
      }

      track({
        action: Action.PRESSED,
        extras: {
          slideId: currentSlide.route,
          gameId: currentSlide.gameId,
          route: currentSlide.route,
          slideIndex: currentIndex,
          control: "FollowButton",
          controlType: "Button",
          screen: "HomePageCarousel",
          userJourney: UserJourney.CAROUSEL,
          followAction: isWishlisted ? "unfollow" : "follow",
        },
      });
    } catch (err) {
      notifyError(err, "riveFollowButton");
    } finally {
      setIsLoading(false);
    }
  };

  const handleClick = () => {
    track({
      action: Action.PRESSED,
      extras: {
        slideId: currentSlide.route,
        slideTitle: currentSlide.description,
        ctaText:
          typeof currentSlide.ctaText === "string"
            ? currentSlide.ctaText
            : "ButtonCTAContent",
        route: currentSlide.route,
        slideIndex: currentIndex,
        control:
          typeof currentSlide.ctaText === "string"
            ? currentSlide.ctaText
            : "CarouselButton",
        controlType: "Button",
        screen: "HeroCarousel",
        userJourney: UserJourney.CAROUSEL,
      },
    });

    if (currentSlide.route) {
      router.push(currentSlide.route);
    }
  };

  return (
    <div className="flex flex-col relative items-center max-w-2xl md:items-start md:pl-2 gap-4">
      <div className="h-24 w-full md:h-32 flex justify-center items-end md:justify-start overflow-hidden">
        {currentSlide.logoUrl && (
          <Image
            src={currentSlide.logoUrl}
            alt={`${currentSlide.logoUrl} logo`}
            width={600}
            height={200}
            className="cursor-pointer object-contain max-h-full"
            onClick={handleClick}
          />
        )}
      </div>

      <div className="h-32 md:h-20 w-full flex flex-col items-center md:items-start justify-start">
        {currentSlide.title && (
          <p
            className={`${headingPrimary.className} text-2xl md:text-3xl font-bold whitespace-pre-line text-center md:text-left`}
          >
            {currentSlide.title.replace(/ \| /g, "\n").replace(/\\n/g, "\n")}
          </p>
        )}
      </div>

      <div className="hidden md:flex h-6 w-full flex-col items-start justify-start">
        {currentSlide.description && (
          <p
            className={`${headingPrimary.className} text-lg font-medium whitespace-pre-line text-left text-white/90 max-w-md`}
          >
            {currentSlide.description
              .replace(/ \| /g, "\n")
              .replace(/\\n/g, "\n")}
          </p>
        )}
      </div>

      <div className="flex items-center gap-3 justify-center md:justify-start">
        {currentSlide.gameId ? (
          <Button
            size="lg"
            className="px-4 py-2 text-sm md:px-10 md:py-3 md:text-base rounded-lg shadow-sm"
            onClick={handleFollowToggle}
            disabled={isLoading || isWishlisting}
            aria-label={isWishlisted ? "Following" : "Follow"}
          >
            <div className="flex items-center gap-2 md:gap-3">
              {isLoading || isWishlisting ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Heart
                  className={`w-5 h-5 ${isWishlisted ? "fill-current" : ""}`}
                />
              )}
              <span className="font-medium">
                {isWishlisted ? "Following" : "Follow"}
              </span>
            </div>
          </Button>
        ) : (
          <Button
            size="lg"
            className="px-4 py-2 text-sm md:px-10 md:py-3 md:text-base rounded-lg shadow-sm"
            onClick={handleClick}
          >
            <div className="flex items-center gap-2 md:gap-3">
              <span className="font-medium">{currentSlide.ctaText}</span>
            </div>
          </Button>
        )}
        {currentSlide.gameSlug && (
          <Button
            size="lg"
            className="px-4 py-2 text-sm md:px-10 md:py-3 md:text-base rounded-lg shadow-sm"
            onClick={() => {
              track({
                action: Action.PRESSED,
                extras: {
                  slideId: currentSlide.route,
                  gameId: currentSlide.gameId,
                  gameSlug: currentSlide.gameSlug,
                  route: currentSlide.route,
                  slideIndex: currentIndex,
                  control: "QuestNowButton",
                  controlType: "Button",
                  screen: "HomePageCarousel",
                  userJourney: UserJourney.CAROUSEL,
                },
              });
              router.push(currentSlide.route);
            }}
          >
            <div className="flex items-center gap-2 md:gap-3">
              <Gamepad2 className="w-4 h-4 md:w-5 md:h-5" />
              <span className="font-medium">Quest Now</span>
              {(currentSlide.gemsAmount ?? 0) > 0 && (
                <GemsBadge gems={currentSlide.gemsAmount} />
              )}
            </div>
          </Button>
        )}
      </div>
    </div>
  );
};
